import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface AccentColorContextType {
  accentColor: string;
  setAccentColor: (color: string) => void;
}

const AccentColorContext = createContext<AccentColorContextType | undefined>(undefined);

const ACCENT_COLORS = {
  teal: { base: '#14B8A6', hover: '#0D9488', light: '#5EEAD4' },
  emerald: { base: '#10B981', hover: '#059669', light: '#6EE7B7' },
  cyan: { base: '#06B6D4', hover: '#0891B2', light: '#67E8F9' },
  sky: { base: '#0EA5E9', hover: '#0284C7', light: '#7DD3FC' },
  violet: { base: '#8B5CF6', hover: '#7C3AED', light: '#C4B5FD' },
  pink: { base: '#EC4899', hover: '#DB2777', light: '#F9A8D4' },
  red: { base: '#EF4444', hover: '#DC2626', light: '#FCA5A5' },
  amber: { base: '#F59E0B', hover: '#D97706', light: '#FCD34D' },
  lime: { base: '#84CC16', hover: '#65A30D', light: '#BEF264' },
  slate: { base: '#64748B', hover: '#475569', light: '#CBD5E1' },
};

export function AccentColorProvider({ children }: { children: ReactNode }) {
  const [accentColor, setAccentColorState] = useState<string>(() => {
    return localStorage.getItem('accentColor') || 'teal';
  });

  useEffect(() => {
    const colors = ACCENT_COLORS[accentColor as keyof typeof ACCENT_COLORS] || ACCENT_COLORS.teal;
    
    document.documentElement.style.setProperty('--accent', colors.base);
    document.documentElement.style.setProperty('--accent-hover', colors.hover);
    document.documentElement.style.setProperty('--accent-light', colors.light);
  }, [accentColor]);

  const setAccentColor = (color: string) => {
    setAccentColorState(color);
    localStorage.setItem('accentColor', color);
  };

  return (
    <AccentColorContext.Provider value={{ accentColor, setAccentColor }}>
      {children}
    </AccentColorContext.Provider>
  );
}

export function useAccentColor() {
  const context = useContext(AccentColorContext);
  if (context === undefined) {
    throw new Error('useAccentColor must be used within an AccentColorProvider');
  }
  return context;
}